import { DataGrid } from "@mui/x-data-grid";
import { Table, TableBody, TableCell, TableContainer, TableRow, Paper } from "@mui/material";
import "../HealthStatus.scss";
import PropTypes from 'prop-types';
import healthy from "../../../assets/images/healthy.svg";
import warning from "../../../assets/images/warning.png";
import critical from "../../../assets/images/critical.svg";
import LoaderOverlay from "../../Overlay";

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

const getStatusIcon = (status) => {
  if (!status || typeof status !== 'string') {
    return critical; // Default to critical if status is invalid
  }
  
  const statusLower = status.toLowerCase();
  if (statusLower === "healthy" || statusLower === "active") {
    return healthy;
  } else if (statusLower === "warning") {
    return warning;
  } else {
    return critical;
  }
};

const DetailedViewHealthCard = ({ detailedViewCards, additonalText = "", loading }) => {
  if (!detailedViewCards || loading || (typeof detailedViewCards !== "object" && !Array.isArray(detailedViewCards))) {
    return (
      <LoaderOverlay />
    );
  }

  let processedData = detailedViewCards;
  
  // Process UPS data
  if (!Array.isArray(detailedViewCards) && Object.keys(detailedViewCards)) {
    processedData = Object.entries(detailedViewCards).map(([key, value], index) => ({
      id: index + 1,
      parameter: key,
      value: value
    }));
  }
  const dataArray = Array.isArray(processedData) ? processedData : [processedData];
  if (dataArray.length === 0) {
    return (
      <LoaderOverlay />
    );
  }

  let columns;
 if (additonalText.toLowerCase() === "dmp" || additonalText === "Strobes & Speakers" || additonalText.toLowerCase() === "peplink") {
    columns = [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        sortable: false,
        disableColumnMenu: true
      },
      {
        field: 'health',
        headerName: 'Health',
        flex: 1,
      }
    ];
  }
    else if (additonalText === "ecc") {
    columns = [
      {
        field: 'parameter',
        headerName: 'Component',
        flex: 1,
        sortable: false,
        disableColumnMenu: true
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const statusValue = params.value || 'Unknown';
          const statusIcon = getStatusIcon(statusValue);
          return (
            <div className={statusValue === "Healthy" ? "status-green" : "status-red"} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <img src={statusIcon} alt="" style={{ width: '20px', height: '20px' }} />
              {statusValue}
            </div>
          );
        }
      }
    ];
  }
  // Push Buttons specific columns  
  else if (additonalText === "pushbuttons") {
    columns = [
      {
        field: 'name',
        headerName: 'Device Name',
        flex: 1,
        sortable: false,
        disableColumnMenu: true
      },
      {
        field: 'healthStatus',
        headerName: 'Status',
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const statusValue = params.row.health;
          const statusIcon = getStatusIcon(statusValue ? "healthy" : "critical");
          return (
            <div className={statusValue ? "status-green" : "status-red"} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <img src={statusIcon} alt={statusValue ? "Healthy" : "Critical"} style={{ width: '25px', height: '25px' }} />
              {statusValue}
            </div>
          );
        }
      }
    ];
  }
  // Generic key-value format (like UPS data)
  else if (processedData !== detailedViewCards || (Array.isArray(processedData) && processedData.length > 0 && 'parameter' in processedData[0])) {
    columns = [
      {
        field: 'parameter',
        headerName: 'Parameter',
        flex: 1,
        sortable: false,
        disableColumnMenu: true
      },
      {
        field: 'value',
        headerName: 'Value',
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          if (params.row.parameter === "Charge Level") {
            const charge = parseInt(params.value);
            if (!isNaN(charge) && charge < 20) {
              return <div className="status-red">{params.value}</div>;
            }
          }
          return params.value;
        }
      }
    ];
  }
  // Camera data or other structured data
  else {
    const displayFields = additonalText === "camera" ? ["name",  "deviceMake", "macAddress", "ipAddress", "topicName", "dateInstalled", "status"] : Object.keys(dataArray[0]).filter((key) => key !== "streamUrl");
    columns = displayFields.map((key) => ({
      field: key,
      headerName: key === "name" ? "Camera Name" : key.replace(/([A-Z])/g, " $1").split(" ").map(capitalize).join(" "),
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
       minWidth: 150,
      renderCell: (params) => {
        if (key === "status") {
          const statusValue = params.value || 'Unknown';
          const statusIcon = getStatusIcon(statusValue);
          return (
            <div className={statusValue === "Active" ? "status-green" : "status-red"} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <img src={statusIcon} alt="" style={{ width: '20px', height: '20px' }} />
              {statusValue}
            </div>
          );
        }
        return params.value;
      },
    }));
  }
  // const rows = dataArray.map((item, index) => ({
  //   id: item.id || index + 1,
  //   ...item,
  // }));
  const rows = additonalText === "pushbuttons"
    ? dataArray.filter((item) => item && item.name &&
            typeof item.health === "boolean"
        ).map((item, index) => ({
          id: item.id ?? index + 1,
          ...item,
        }))
    : dataArray.map((item, index) => ({
        id: item.id ?? index + 1,
        ...item,
      }));
  if(processedData.node) {
    return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableBody>
          {columns.map(({ field, headerName }) => (
            <TableRow key={field}>
              <TableCell variant="head" sx={{ fontWeight: 'bold' }}>{headerName}</TableCell>
              <TableCell>{rows[0][field]}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
  } 
  return (
    <>
    {
      additonalText === "pushbuttons" && (
         <div className="visibleInformation" aria-hidden="true">
              <span><img src={healthy} alt="" className="status-icon" />Healthy</span>
                <span><img src={critical} alt="" className="status-icon" />Critical</span>
          </div>
      )
    }
     
      <div className="detailedViewCards">
      <DataGrid
        rows={rows}
        columns={columns}
        pageSize={5}
        rowsPerPageOptions={[5]}
        disableSelectionOnClick
        disableRowSelectionOnClick
        pagination
        hideFooter
      />
    </div>
    </>
  );
};

DetailedViewHealthCard.propTypes = {
  additonalText: PropTypes.string,
  detailedViewCards: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.object,
  ]).isRequired,
};

export default DetailedViewHealthCard;